// Per ADR-007 amendment (x-001m): role + orgId come from the JWT claim,
// parsed once at sign-in. /api/v1/admin/me is informational (display
// name + soft-delete check) and is NOT the source of role.
//
// This module decodes the JWT payload locally (no signature verification
// — the backend already verified before issuing). When the FE swaps to
// real Neon SSO, the same decode path runs against the SSO-issued JWT.
import type { AdminMe, AdminRole, SupervisorMe } from '@neon-one/auctions-types';

import { useAdminSession } from '@/shell/stores/adminSession';
import { httpClient } from '@/shell/http';
import { isOrgSlug } from '@/shell/org';

interface AdminJwtPayload {
  sub?: string;
  role?: AdminRole;
  orgId?: number;
  org_id?: number;
  salesforceId?: string;
  salesforce_id?: string;
  /** Bound Neon SSO organization id (session JWTs minted after org-adopt / callback bind). */
  ssoOrgId?: number;
  sso_org_id?: number;
  displayName?: string;
  email?: string;
  exp?: number;
  iat?: number;
  is_supervisor?: boolean;
  is_sandbox?: boolean;
  /** Session was bound through the multi-org picker. */
  multi_org?: boolean;
}

/** Options that tighten meFromClaims / adoptJwt for multi-org bind paths. */
export interface MeFromClaimsOptions {
  /**
   * Org slug of the current hostname (`getOrgSlug()`). When non-empty, the
   * bound tenant (`fallback.orgSlug` from org-adopt / callback) must match —
   * this is how the client verifies that the JWT's `sso_org_id` resolved to
   * the tenant mapped to the current `{org_slug}` subdomain without keeping a
   * client-side id→slug map.
   */
  currentOrgSlug?: string;
  /**
   * `sso_org_id` the client asked to bind (Portal query). When set, the JWT
   * must carry the same `sso_org_id` claim.
   */
  expectedSsoOrgId?: number | null;
}

/** Decode the JWT payload (base64url) without verifying — server has already verified. */
export function decodeJwtPayload(jwt: string): AdminJwtPayload | null {
  const parts = jwt.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '=='.slice(0, (4 - (base64.length % 4)) % 4);
    // Use TextDecoder for proper UTF-8 handling (atob alone mangles non-ASCII chars)
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as AdminJwtPayload;
  } catch {
    return null;
  }
}

/** Check whether a raw JWT string is expired (treats tokens expiring within 30s as already expired). */
export function isJwtExpired(jwt: string): boolean {
  const payload = decodeJwtPayload(jwt);
  if (typeof payload?.exp !== 'number' || !Number.isFinite(payload.exp)) return true;
  return payload.exp * 1000 <= Date.now() + 30_000;
}

/** Read the bound `sso_org_id` claim from a session JWT (camel or snake). */
export function ssoOrgIdFromJwt(jwtStr: string): number | null {
  const payload = decodeJwtPayload(jwtStr);
  if (!payload) return null;
  const raw = payload.sso_org_id ?? payload.ssoOrgId;
  return typeof raw === 'number' && Number.isInteger(raw) && raw > 0 ? raw : null;
}

/**
 * Multi-org bind guard for session JWTs.
 *
 * When `options.expectedSsoOrgId` is set, the JWT must carry that
 * `sso_org_id`. When `options.currentOrgSlug` is a live subdomain label,
 * `fallbackOrgSlug` (the tenant the server bound that sso_org_id to) must
 * equal it — mismatch means the bound org is not the tenant on this host.
 * Callers clear the session and recover to the in-app org picker (or sign-in) on false.
 */
export function passesMultiOrgBindGuard(
  jwtStr: string,
  fallbackOrgSlug: string | null | undefined,
  options?: MeFromClaimsOptions,
): boolean {
  if (options?.expectedSsoOrgId != null) {
    const boundSsoOrgId = ssoOrgIdFromJwt(jwtStr);
    if (boundSsoOrgId == null || boundSsoOrgId !== options.expectedSsoOrgId) {
      return false;
    }
  }

  const currentSlug = options?.currentOrgSlug;
  if (typeof currentSlug === 'string' && currentSlug !== '' && isOrgSlug(currentSlug)) {
    // Server mapped sso_org_id → tenant → orgSlug. If that slug is known and
    // disagrees with the host, the bind is for a different org — reject.
    if (isOrgSlug(fallbackOrgSlug) && fallbackOrgSlug !== currentSlug) {
      return false;
    }
  }

  return true;
}

/**
 * Project AdminMe from a verified session JWT (identity fields only).
 *
 * Multi-org bind checks live in {@link passesMultiOrgBindGuard}; this
 * function returns null when that guard rejects, then projects claims +
 * fallback display fields into AdminMe.
 */
export function meFromClaims(
  jwtStr: string,
  fallback?: Partial<AdminMe>,
  options?: MeFromClaimsOptions,
): AdminMe | null {
  const payload = decodeJwtPayload(jwtStr);
  if (!payload) return null;

  // sub and role must come from the JWT itself — fallback cannot supply them.
  const sub = payload.sub;
  const role = payload.role;
  if (!sub || !role) return null;

  const orgId = payload.orgId ?? payload.org_id ?? fallback?.orgId;
  const salesforceId = payload.salesforceId ?? payload.salesforce_id ?? fallback?.salesforceId;

  if (orgId == null || !salesforceId) return null;

  // Not a JWT claim — populated from /admin/me or the bind response; null until then.
  const orgSlug = fallback?.orgSlug ?? null;
  if (!passesMultiOrgBindGuard(jwtStr, orgSlug, options)) return null;

  const displayName = (typeof payload.displayName === 'string' ? payload.displayName : null)
    ?? fallback?.displayName ?? null;
  const email = (typeof payload.email === 'string' ? payload.email : null)
    ?? fallback?.email ?? null;

  const isSupervisor = payload.is_supervisor === true || fallback?.isSupervisor === true;

  // Tri-state on purpose. `undefined` means the token predates the claim (or is
  // an SSO id_token, which has no such claim) — NOT that the org is a real
  // customer. Consumers that exclude sandbox orgs must require an explicit
  // false, so leave the field off entirely rather than defaulting it.
  const isSandbox = typeof payload.is_sandbox === 'boolean' ? payload.is_sandbox : fallback?.isSandbox;

  return {
    id: fallback?.id ?? 0,
    sub,
    role,
    orgId,
    salesforceId,
    orgSlug: orgSlug ?? null,
    displayName,
    email,
    ...(isSupervisor ? { isSupervisor: true } : {}),
    ...(typeof isSandbox === 'boolean' ? { isSandbox } : {}),
  };
}

/**
 * Adopt a fresh JWT into the session, projecting the admin identity
 * from JWT claims. Login flows call this on a successful auth response.
 * The optional `fallback` is the API-returned AdminMe (for fields like
 * id + displayName the JWT may omit).
 *
 * Returns false when claims are incomplete OR when multi-org bind options
 * detect an sso_org_id / org_slug mismatch (caller must clear + recover).
 */
export function adoptJwt(
  jwt: string,
  fallback?: Partial<AdminMe>,
  options?: MeFromClaimsOptions,
): boolean {
  const me = meFromClaims(jwt, fallback, options);
  if (!me) return false;
  useAdminSession().setSession(jwt, me);
  return true;
}

export function supervisorFromClaims(jwtStr: string): SupervisorMe | null {
  const payload = decodeJwtPayload(jwtStr);
  if (!payload?.is_supervisor || !payload.sub) return null;
  // JWT with org_id is a supervisor who assumed an org — adopt as admin, not supervisor.
  if (payload.org_id) return null;
  return {
    sub: payload.sub,
    email: payload.email ?? '',
    displayName: payload.displayName ?? null,
  };
}

export function adoptSupervisorJwt(jwt: string, supervisor: SupervisorMe): boolean {
  useAdminSession().setSupervisorSession(jwt, supervisor);
  return true;
}

/**
 * Refresh the cached display name + soft-delete status by calling
 * /api/v1/admin/me. Role is NOT updated from this response — the JWT
 * claim is authoritative per ADR-007 amendment.
 */
export async function refreshDisplayInfo(): Promise<void> {
  const session = useAdminSession();
  const current = session.user;
  if (!current) return;
  try {
    const resp = await httpClient().get<AdminMe>('/admin/me');
    session.setSession(session.getJwt() ?? '', {
      ...current,
      displayName: resp.data.displayName,
      email: resp.data.email,
      orgSlug: resp.data.orgSlug,
      orgName: resp.data.orgName,
      isSandbox: resp.data.isSandbox,
      // role + orgId stay from the JWT claim
    });
  } catch {
    // /me is informational; failures don't sign the user out
  }
}
